import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  contactProblems, draftFromContact, getPublicContact, savePublicContact, type ContactDraft,
} from "../../api/contact";

const refusal = (e: unknown) =>
  e instanceof ApiError && e.kind === "forbidden"
    ? "Refused: your role may not change the portal contact for this namespace, or it is outside your subtree."
    : e instanceof Error
      ? e.message
      : String(e);

/**
 * The contact shown at the bottom of every page of the captive portal: address,
 * phone, email and one link to the provider's website.
 *
 * 🔑 NOT THE REGISTERED ADDRESS. This card writes radaccess's `portal` contact
 * rows through the operatorcontact grant an Operator holds. The registered
 * address printed on invoices is a separate record, on the profile below, and
 * only a role that may edit provider records can change it.
 */
export function PortalContactCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const load = useQuery({
    queryKey: ["publicContact", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await getPublicContact(identity, ns)).data,
  });
  const [draft, setDraft] = useState<ContactDraft>(() => draftFromContact(null));
  useEffect(() => {
    if (load.data !== undefined) setDraft(draftFromContact(load.data));
  }, [load.data]);

  const save = useMutation({
    mutationFn: async () => (await savePublicContact(identity, ns, draft)).data,
    onSuccess: (c) => {
      qc.setQueryData(["publicContact", ns], c);
    },
  });

  const problems = contactProblems(draft);
  const field = (name: keyof ContactDraft, label: string, extra: { helperText?: string; placeholder?: string } = {}) => ({
    fullWidth: true,
    size: "small" as const,
    label,
    value: draft[name],
    onChange: (e: { target: { value: string } }) => {
      save.reset();
      setDraft({ ...draft, [name]: e.target.value });
    },
    error: Boolean(problems[name]),
    helperText: problems[name] ?? extra.helperText ?? " ",
    ...(extra.placeholder ? { placeholder: extra.placeholder } : {}),
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Contact on your portal</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Shown at the bottom of every page of your captive portal for <b>{ns}</b>. A guest who cannot get online can still call
        or email you from it. Leave a line empty to hide it.
      </Typography>

      {load.isError && (
        <Alert severity={load.error instanceof ApiError && load.error.kind === "forbidden" ? "warning" : "error"} sx={{ mb: 2 }}>
          <AlertTitle>Could not load the contact guests see</AlertTitle>
          {refusal(load.error)}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField {...field("address", "Address")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField {...field("city", "City")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField {...field("state", "State")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField {...field("pin", "PIN")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField {...field("country", "Country")} />
        </Grid>
        <Grid size={{ xs: 4, sm: 2 }}>
          <TextField {...field("countrycode", "Code", { placeholder: "+91" })} />
        </Grid>
        <Grid size={{ xs: 8, sm: 4 }}>
          <TextField {...field("phone", "Phone", { helperText: "Guests can tap to call" })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField {...field("email", "Email")} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            {...field("website", "Website", {
              placeholder: "https://www.example.in",
              helperText: "One link in the footer. It opens once the guest is online, unless your router lets it through before sign-in.",
            })}
          />
        </Grid>
      </Grid>

      {save.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <AlertTitle>Not saved</AlertTitle>
          {refusal(save.error)}
        </Alert>
      )}
      {save.isSuccess && <Alert severity="success" sx={{ mt: 1 }}>Saved. Guests see it on their next portal load, within a minute.</Alert>}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button variant="contained" disabled={Object.keys(problems).length > 0 || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save contact"}
        </Button>
      </Stack>
    </Paper>
  );
}
